import { useState, useMemo, useEffect } from "react";
import { fetchJson, saveJson, getCachedJson } from "./s3Client";
import { useIsMobile } from "../useIsMobile";

// ─── Seed Data ────────────────────────────────────────────────────────────────
const SEED_ACCOUNTS = [
  { id: 1, name: "Chase Sapphire", type: "credit", balance: 6420, limit: 10000, apr: 26.99, minPayment: 250, dueDay: 7, color: "#E24B4A" },
  { id: 2, name: "Citi Double Cash", type: "credit", balance: 4900, limit: 8500, apr: 23.9, minPayment: 200, dueDay: 14, color: "#EF9F27" },
  { id: 3, name: "Amex Blue Cash", type: "credit", balance: 3500, limit: 12000, apr: 19.9, minPayment: 180, dueDay: 22, color: "#1D9E75" },
  { id: 4, name: "Chase Checking", type: "bank", balance: 3210, limit: null, apr: null, minPayment: null, dueDay: null, color: "#378ADD" },
];

const SEED_TRANSACTIONS = [
  { id: 1, accountId: 1, date: "2026-05-01", amount: 85.40, category: "Dining", description: "Joe's KC BBQ" },
  { id: 2, accountId: 1, date: "2026-05-02", amount: 120.00, category: "Groceries", description: "Hen House" },
  { id: 3, accountId: 2, date: "2026-05-02", amount: 55.00, category: "Gas", description: "QuikTrip" },
  { id: 4, accountId: 2, date: "2026-05-03", amount: 14.99, category: "Subscriptions", description: "Netflix" },
  { id: 5, accountId: 3, date: "2026-05-03", amount: 210.00, category: "Shopping", description: "Amazon" },
  { id: 6, accountId: 1, date: "2026-05-04", amount: 42.00, category: "Dining", description: "Fric-N-Frac" },
  { id: 7, accountId: 2, date: "2026-05-04", amount: 95.00, category: "Groceries", description: "Price Chopper" },
  { id: 8, accountId: 3, date: "2026-05-05", amount: 38.00, category: "Gas", description: "Phillips 66" },
  { id: 9, accountId: 1, date: "2026-05-05", amount: 9.99, category: "Subscriptions", description: "Spotify" },
  { id: 10, accountId: 2, date: "2026-05-06", amount: 320.00, category: "Dining", description: "Corvino" },
];

const CATEGORIES = ["Dining", "Groceries", "Gas", "Subscriptions", "Shopping", "Travel", "Healthcare", "Utilities", "Other"];
const ACCOUNT_COLORS = ["#E24B4A", "#EF9F27", "#1D9E75", "#378ADD", "#7F77DD", "#D85A30", "#D4537E"];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function monthsToPayoff(balance, apr, monthlyPayment) {
  const r = apr / 100 / 12;
  if (r === 0) return Math.ceil(balance / monthlyPayment);
  if (monthlyPayment <= balance * r) return Infinity;
  return Math.ceil(Math.log(monthlyPayment / (monthlyPayment - balance * r)) / Math.log(1 + r));
}

function payoffDate(months) {
  if (!isFinite(months)) return "Never";
  const d = new Date(2026, 4, 1);
  d.setMonth(d.getMonth() + months);
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function fmt(n) {
  if (n == null || isNaN(n)) return "$0";
  return Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  root: {
    fontFamily: "'DM Mono', 'Courier New', monospace",
    background: "#0f0f11",
    minHeight: "100vh",
    color: "#e8e6e0",
    padding: "0",
    paddingTop: 70,
  },
  topbar: {
    background: "#161618",
    borderBottom: "1px solid #2a2a2e",
    padding: "18px 32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "sticky",
    top: 70,
    zIndex: 99,
  },
  topbarTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 22,
    fontWeight: 600,
    margin: 0,
    letterSpacing: "-0.02em",
    color: "#f0ede6",
  },
  topbarSub: {
    fontSize: 11,
    color: "#666",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginTop: 3,
  },
  tabRow: {
    display: "flex",
    gap: 4,
    background: "#1a1a1d",
    border: "1px solid #2a2a2e",
    borderRadius: 8,
    padding: 3,
  },
  tab: (active) => ({
    fontSize: 12,
    padding: "6px 16px",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    background: active ? "#2a2a2e" : "transparent",
    color: active ? "#f0ede6" : "#666",
    transition: "all 0.15s",
    letterSpacing: "0.04em",
  }),
  page: { padding: "28px 32px", maxWidth: 1100, margin: "0 auto" },
  grid4: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 12,
    marginBottom: 20,
  },
  statCard: {
    background: "#161618",
    border: "1px solid #2a2a2e",
    borderRadius: 10,
    padding: "16px 18px",
  },
  statLabel: { fontSize: 10, color: "#555", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8 },
  statValue: (color) => ({ fontSize: 22, fontWeight: 500, color: color || "#f0ede6" }),
  statSub: { fontSize: 11, color: "#555", marginTop: 4 },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 },
  card: {
    background: "#161618",
    border: "1px solid #2a2a2e",
    borderRadius: 12,
    padding: "18px 20px",
  },
  cardTitle: { fontSize: 10, color: "#555", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 14 },
  acctRow: {
    paddingBottom: 12,
    marginBottom: 12,
    borderBottom: "1px solid #222",
  },
  acctTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 },
  acctName: { fontSize: 13, fontWeight: 500, color: "#e8e6e0" },
  badge: (color, bg) => ({
    fontSize: 10,
    padding: "2px 8px",
    borderRadius: 20,
    background: bg,
    color: color,
    letterSpacing: "0.04em",
  }),
  acctNums: { display: "flex", justifyContent: "space-between", fontSize: 11, color: "#666", marginBottom: 6 },
  barBg: { height: 4, background: "#222", borderRadius: 2 },
  barFill: (w, color) => ({ height: 4, borderRadius: 2, width: `${w}%`, background: color, transition: "width 0.4s" }),
  payoffItem: { marginBottom: 14 },
  payoffTop: { display: "flex", justifyContent: "space-between", marginBottom: 5 },
  payoffName: { fontSize: 13, fontWeight: 500 },
  payoffDate: { fontSize: 11, color: "#666" },
  payoffBarBg: { height: 5, background: "#222", borderRadius: 3, marginBottom: 4 },
  payoffMeta: { display: "flex", justifyContent: "space-between", fontSize: 10, color: "#555" },
  catRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
  catLabel: { fontSize: 11, color: "#888", width: 90, flexShrink: 0 },
  catBarBg: { flex: 1, height: 5, background: "#222", borderRadius: 3 },
  catAmt: { fontSize: 11, textAlign: "right", width: 52, flexShrink: 0, color: "#e8e6e0" },
  txTable: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  txTh: {
    textAlign: "left",
    fontSize: 10,
    color: "#555",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    paddingBottom: 10,
    borderBottom: "1px solid #222",
    fontWeight: 400,
  },
  txTd: { padding: "9px 6px", borderBottom: "1px solid #1a1a1d", color: "#b8b5ae", verticalAlign: "middle" },
  btn: (variant) => ({
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    padding: "7px 14px",
    borderRadius: 7,
    border: variant === "primary" ? "1px solid #3a3a3e" : "1px solid #2a2a2e",
    background: variant === "primary" ? "#2a2a2e" : "transparent",
    color: variant === "danger" ? "#E24B4A" : "#b8b5ae",
    cursor: "pointer",
    letterSpacing: "0.04em",
    transition: "all 0.15s",
  }),
  btnRow: { display: "flex", gap: 8, marginTop: 14 },
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  modal: {
    background: "#1a1a1d",
    border: "1px solid #2a2a2e",
    borderRadius: 14,
    padding: "28px 28px",
    width: 440,
    maxHeight: "90vh",
    overflowY: "auto",
  },
  modalTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 18,
    fontWeight: 600,
    color: "#f0ede6",
    marginBottom: 20,
  },
  formGroup: { marginBottom: 14 },
  label: { display: "block", fontSize: 10, color: "#666", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 },
  input: {
    width: "100%",
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    padding: "9px 11px",
    color: "#e8e6e0",
    fontSize: 13,
    fontFamily: "'DM Mono', monospace",
    boxSizing: "border-box",
    outline: "none",
  },
  select: {
    width: "100%",
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    padding: "9px 11px",
    color: "#e8e6e0",
    fontSize: 13,
    fontFamily: "'DM Mono', monospace",
    boxSizing: "border-box",
    outline: "none",
    cursor: "pointer",
  },
  colorDot: (c, selected) => ({
    width: 22, height: 22, borderRadius: "50%", background: c, cursor: "pointer",
    border: selected ? "2px solid #f0ede6" : "2px solid transparent",
    display: "inline-block", marginRight: 6, flexShrink: 0,
  }),
  sliderRow: { display: "flex", alignItems: "center", gap: 10 },
};

// ─── Modal: Add/Edit Account ──────────────────────────────────────────────────
function AccountModal({ account, onSave, onClose }) {
  const blank = { name: "", type: "credit", balance: "", limit: "", apr: "", minPayment: "", dueDay: "", color: ACCOUNT_COLORS[0] };
  const [form, setForm] = useState(account ? { ...account, balance: String(account.balance), limit: String(account.limit || ""), apr: String(account.apr || ""), minPayment: String(account.minPayment || ""), dueDay: String(account.dueDay || "") } : blank);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isCredit = form.type === "credit";
  const isDebt = form.type === "credit" || form.type === "loan";

  function save() {
    if (!form.name.trim()) return;
    onSave({
      ...form,
      id: account?.id ?? Date.now(),
      balance: parseFloat(form.balance) || 0,
      limit: isCredit ? (parseFloat(form.limit) || null) : null,
      apr: isDebt ? (parseFloat(form.apr) || null) : null,
      minPayment: isDebt ? (parseFloat(form.minPayment) || null) : null,
      dueDay: isDebt ? (parseInt(form.dueDay) || null) : null,
    });
  }

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <p style={S.modalTitle}>{account ? "Edit Account" : "Add Account"}</p>

        <div style={S.formGroup}>
          <label style={S.label}>Account Name</label>
          <input style={S.input} value={form.name} onChange={e => set("name", e.target.value)} placeholder="Chase Sapphire Reserve" />
        </div>

        <div style={S.formGroup}>
          <label style={S.label}>Account Type</label>
          <select style={S.select} value={form.type} onChange={e => set("type", e.target.value)}>
            <option value="credit">Credit Card</option>
            <option value="loan">Car Loan</option>
            <option value="bank">Bank Account</option>
          </select>
        </div>

        <div style={S.formGroup}>
          <label style={S.label}>Current Balance ($)</label>
          <input style={S.input} type="number" value={form.balance} onChange={e => set("balance", e.target.value)} placeholder="0.00" />
        </div>

        {isDebt && <>
          {isCredit && <div style={S.formGroup}>
            <label style={S.label}>Credit Limit ($)</label>
            <input style={S.input} type="number" value={form.limit} onChange={e => set("limit", e.target.value)} placeholder="10000" />
          </div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={S.formGroup}>
              <label style={S.label}>APR (%)</label>
              <input style={S.input} type="number" value={form.apr} onChange={e => set("apr", e.target.value)} placeholder="24.99" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Monthly Payment ($)</label>
              <input style={S.input} type="number" value={form.minPayment} onChange={e => set("minPayment", e.target.value)} placeholder="350" />
            </div>
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Payment Due Day (1–31)</label>
            <input style={S.input} type="number" min="1" max="31" value={form.dueDay} onChange={e => set("dueDay", e.target.value)} placeholder="15" />
          </div>
        </>}

        <div style={S.formGroup}>
          <label style={S.label}>Color</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
            {ACCOUNT_COLORS.map(c => (
              <span key={c} style={S.colorDot(c, form.color === c)} onClick={() => set("color", c)} />
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
          <button style={S.btn("primary")} onClick={save}>{account ? "Save Changes" : "Add Account"}</button>
          <button style={S.btn()} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Add/Edit Transaction ──────────────────────────────────────────────
function TxModal({ tx, accounts, onSave, onClose }) {
  const blank = { accountId: accounts[0]?.id ?? 1, date: new Date().toISOString().slice(0, 10), amount: "", category: "Dining", description: "" };
  const [form, setForm] = useState(tx ? { ...tx, amount: String(tx.amount) } : blank);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function save() {
    if (!form.description.trim() || !form.amount) return;
    onSave({ ...form, id: tx?.id ?? Date.now(), accountId: parseInt(form.accountId), amount: parseFloat(form.amount) });
  }

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <p style={S.modalTitle}>{tx ? "Edit Transaction" : "Add Transaction"}</p>

        <div style={S.formGroup}>
          <label style={S.label}>Account</label>
          <select style={S.select} value={form.accountId} onChange={e => set("accountId", e.target.value)}>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={S.formGroup}>
            <label style={S.label}>Date</label>
            <input style={S.input} type="date" value={form.date} onChange={e => set("date", e.target.value)} />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Amount ($)</label>
            <input style={S.input} type="number" value={form.amount} onChange={e => set("amount", e.target.value)} placeholder="0.00" />
          </div>
        </div>

        <div style={S.formGroup}>
          <label style={S.label}>Category</label>
          <select style={S.select} value={form.category} onChange={e => set("category", e.target.value)}>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>

        <div style={S.formGroup}>
          <label style={S.label}>Description</label>
          <input style={S.input} value={form.description} onChange={e => set("description", e.target.value)} placeholder="Merchant name..." />
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
          <button style={S.btn("primary")} onClick={save}>{tx ? "Save Changes" : "Add Transaction"}</button>
          <button style={S.btn()} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function FinanceDashboard() {
  const isMobile = useIsMobile();
  const [accounts, setAccounts] = useState(() => getCachedJson("accounts.json", []));
  const [transactions, setTransactions] = useState(() => getCachedJson("transactions.json", []));
  const [activeTab, setActiveTab] = useState("overview");
  const [modal, setModal] = useState(null);
  const [simPayment, setSimPayment] = useState(250);
  const [simAcctId, setSimAcctId] = useState(1);
  const [loading, setLoading] = useState(() => !getCachedJson("accounts.json", null));
  const [error, setError] = useState(null);
  const [budget, setBudget] = useState(() => getCachedJson("budget.json", { income: 0, limits: {} }));
  const [budgetSaveStatus, setBudgetSaveStatus] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const [accts, txs, bgt] = await Promise.all([
          fetchJson("accounts.json", null),
          fetchJson("transactions.json", null),
          fetchJson("budget.json", null),
        ]);
        if (accts !== null) {
          setAccounts(accts);
          const firstDebt = accts.find(a => a.type === "credit" || a.type === "loan");
          setSimAcctId(firstDebt?.id ?? 1);
          setSimPayment(firstDebt?.minPayment ?? 100);
        }
        if (txs !== null) setTransactions(txs);
        if (bgt !== null) setBudget(bgt || { income: 0, limits: {} });
      } catch (e) {
        console.error("S3 load error:", e);
        setError(`S3 error: ${e.name} — ${e.message}`);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function persistBudget(next) {
    setBudgetSaveStatus("saving");
    try {
      await saveJson("budget.json", next);
      setBudgetSaveStatus("saved");
      setTimeout(() => setBudgetSaveStatus(null), 1500);
    } catch {
      setBudgetSaveStatus("error");
    }
  }

  function setBudgetLimit(cat, val) {
    const next = { ...budget, limits: { ...budget.limits, [cat]: parseFloat(val) || 0 } };
    setBudget(next);
    persistBudget(next);
  }

  function setBudgetIncome(val) {
    const next = { ...budget, income: parseFloat(val) || 0 };
    setBudget(next);
    persistBudget(next);
  }

  const creditAccounts = accounts.filter(a => a.type === "credit" || a.type === "loan");
  const bankAccounts = accounts.filter(a => a.type === "bank");
  const totalDebt = creditAccounts.reduce((s, a) => s + a.balance, 0);
  const totalSpend = transactions.reduce((s, t) => s + t.amount, 0);
  const totalMinDue = creditAccounts.reduce((s, a) => s + (a.minPayment || 0), 0);
  const nextDue = creditAccounts.filter(a => a.dueDay).sort((a, b) => a.dueDay - b.dueDay)[0];
  const totalBank = bankAccounts.reduce((s, a) => s + a.balance, 0);

  const spendByCategory = useMemo(() => {
    const map = {};
    transactions.forEach(t => { map[t.category] = (map[t.category] || 0) + t.amount; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [transactions]);

  const maxCatSpend = spendByCategory[0]?.[1] || 1;

  const CAT_COLORS = { Dining: "#D85A30", Groceries: "#378ADD", Gas: "#EF9F27", Subscriptions: "#7F77DD", Shopping: "#1D9E75", Travel: "#D4537E", Healthcare: "#5DCAA5", Utilities: "#888780", Other: "#555" };

  const weightedAPR = totalDebt > 0
    ? creditAccounts.reduce((s, a) => s + a.apr * a.balance, 0) / totalDebt
    : 0;

  const isAllAccounts = simAcctId === 0;
  const simAcct = isAllAccounts ? null : accounts.find(a => a.id === simAcctId);
  const simBalance = isAllAccounts ? totalDebt : (simAcct?.balance ?? 0);
  const simAPR = isAllAccounts ? weightedAPR : (simAcct?.apr ?? 0);
  const simMin = isAllAccounts ? totalMinDue : (simAcct?.minPayment ?? 0);
  const simColor = isAllAccounts ? "#7F77DD" : (simAcct?.color ?? "#555");
  const simName = isAllAccounts ? "All Accounts" : (simAcct?.name ?? "");
  const simMonths = monthsToPayoff(simBalance, simAPR, simPayment);

  async function saveAccount(acct) {
    const next = accounts.find(a => a.id === acct.id)
      ? accounts.map(a => a.id === acct.id ? acct : a)
      : [...accounts, acct];
    setAccounts(next);
    setModal(null);
    try { await saveJson("accounts.json", next); } catch (e) { setError(`Save error: ${e.name}`); }
  }
  async function deleteAccount(id) {
    const nextAccounts = accounts.filter(a => a.id !== id);
    const nextTx = transactions.filter(t => t.accountId !== id);
    setAccounts(nextAccounts);
    setTransactions(nextTx);
    try {
      await Promise.all([
        saveJson("accounts.json", nextAccounts),
        saveJson("transactions.json", nextTx),
      ]);
    } catch (e) { setError(`Save error: ${e.name}`); }
  }

  async function saveTx(tx) {
    const next = transactions.find(t => t.id === tx.id)
      ? transactions.map(t => t.id === tx.id ? tx : t)
      : [...transactions, tx];
    setTransactions(next);
    setModal(null);
    try { await saveJson("transactions.json", next); } catch (e) { setError(`Save error: ${e.name}`); }
  }
  async function deleteTx(id) {
    const next = transactions.filter(t => t.id !== id);
    setTransactions(next);
    try { await saveJson("transactions.json", next); } catch (e) { setError(`Save error: ${e.name}`); }
  }

  const acctMap = Object.fromEntries(accounts.map(a => [a.id, a]));

  if (loading) return (
    <div style={{ ...S.root, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "#555", fontSize: 13, letterSpacing: "0.1em" }}>Loading financial data…</p>
    </div>
  );

  if (error) return (
    <div style={{ ...S.root, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "#E24B4A", fontSize: 13, maxWidth: 400, textAlign: "center" }}>{error}</p>
    </div>
  );

  return (
    <>
      <div style={S.root}>
        <div style={{ ...S.topbar, flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", gap: isMobile ? 12 : 0, padding: isMobile ? "14px 16px" : S.topbar.padding }}>
          <div>
            <h1 style={S.topbarTitle}>FinTrack</h1>
            <p style={S.topbarSub}>Personal Finance · {accounts.length} accounts</p>
          </div>
          <div style={{ ...S.tabRow, flexWrap: "wrap" }}>
            {["overview", "payoff", "budget"].map(t => (
              <button key={t} style={S.tab(activeTab === t)} onClick={() => setActiveTab(t)}>
                {t === "overview" ? "Overview" : t === "payoff" ? "Payoff Sim" : "Budget"}
              </button>
            ))}
          </div>
        </div>

        <div style={{ ...S.page, padding: isMobile ? "16px 12px" : S.page.padding }}>

          {activeTab === "overview" && <>
            <div style={{ ...S.grid4, gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)" }}>
              <div style={S.statCard}>
                <div style={S.statLabel}>Total Debt</div>
                <div style={S.statValue("#E24B4A")}>{fmt(totalDebt)}</div>
                <div style={S.statSub}>{creditAccounts.length} credit cards</div>
              </div>
              <div style={S.statCard}>
                <div style={S.statLabel}>Monthly Spend</div>
                <div style={S.statValue()}>{fmt(totalSpend)}</div>
                <div style={S.statSub}>{transactions.length} transactions</div>
              </div>
              <div style={S.statCard}>
                <div style={S.statLabel}>Min Payments Due</div>
                <div style={S.statValue("#EF9F27")}>{fmt(totalMinDue)}</div>
                <div style={S.statSub}>{nextDue ? `Next: May ${nextDue.dueDay}` : "—"}</div>
              </div>
              <div style={S.statCard}>
                <div style={S.statLabel}>Bank Balance</div>
                <div style={S.statValue("#1D9E75")}>{fmt(totalBank)}</div>
                <div style={S.statSub}>{bankAccounts.length} account{bankAccounts.length !== 1 ? "s" : ""}</div>
              </div>
            </div>

            <div style={{ ...S.grid2, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
              <div style={S.card}>
                <div style={S.cardTitle}>Accounts</div>
                {accounts.map((a, i) => (
                  <div key={a.id} style={{ ...S.acctRow, ...(i === accounts.length - 1 ? { border: "none", marginBottom: 0, paddingBottom: 0 } : {}) }}>
                    <div style={S.acctTop}>
                      <span style={S.acctName}>{a.name}</span>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={S.badge(a.type === "credit" ? "#993C1D" : a.type === "loan" ? "#5A3D99" : "#3B6D11", a.type === "credit" ? "#2a1410" : a.type === "loan" ? "#1a1028" : "#141f10")}>
                          {a.type === "credit" ? "Card" : a.type === "loan" ? "Loan" : "Bank"}
                        </span>
                        <button style={{ ...S.btn(), padding: "2px 8px", fontSize: 10 }} onClick={() => setModal({ type: "account", data: a })}>Edit</button>
                        <button style={{ ...S.btn("danger"), padding: "2px 8px", fontSize: 10 }} onClick={() => deleteAccount(a.id)}>✕</button>
                      </div>
                    </div>
                    <div style={S.acctNums}>
                      <span>{a.type === "bank" ? `${fmt(a.balance)} available` : `${fmt(a.balance)} owed`}</span>
                      {a.limit && <span>{fmt(a.limit)} limit</span>}
                      {a.apr && <span>{a.apr}% APR</span>}
                      {a.dueDay && <span>Due May {a.dueDay}</span>}
                    </div>
                    {a.type === "credit" && a.limit && (
                      <div style={S.barBg}><div style={S.barFill(Math.min((a.balance / a.limit) * 100, 100), a.color)} /></div>
                    )}
                  </div>
                ))}
                <div style={S.btnRow}>
                  <button style={S.btn("primary")} onClick={() => setModal({ type: "account", data: null })}>+ Add Account</button>
                </div>
              </div>

              <div style={S.card}>
                <div style={S.cardTitle}>Spending by Category</div>
                {spendByCategory.length === 0 && <p style={{ fontSize: 12, color: "#555" }}>No transactions yet.</p>}
                {spendByCategory.map(([cat, amt]) => (
                  <div key={cat} style={S.catRow}>
                    <span style={S.catLabel}>{cat}</span>
                    <div style={S.catBarBg}>
                      <div style={{ height: 5, borderRadius: 3, width: `${(amt / maxCatSpend) * 100}%`, background: CAT_COLORS[cat] || "#555", transition: "width 0.4s" }} />
                    </div>
                    <span style={S.catAmt}>{fmt(amt)}</span>
                  </div>
                ))}

                <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #222" }}>
                  <div style={S.cardTitle}>Upcoming Payments</div>
                  {creditAccounts.filter(a => a.dueDay).sort((a, b) => a.dueDay - b.dueDay).map(a => {
                    const urgency = a.dueDay <= 7 ? { bg: "#2a1410", color: "#E24B4A" } : a.dueDay <= 14 ? { bg: "#1f1a10", color: "#EF9F27" } : { bg: "#0f1a12", color: "#1D9E75" };
                    return (
                      <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, fontSize: 12 }}>
                        <span style={{ color: "#888" }}>{a.name}</span>
                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          <span style={{ color: "#e8e6e0" }}>{fmt(a.minPayment)} min</span>
                          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: urgency.bg, color: urgency.color }}>May {a.dueDay}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>}

          {activeTab === "budget" && (() => {
            const allCats = Array.from(new Set([...CATEGORIES, ...Object.keys(budget.limits)]));
            const totalBudgeted = allCats.reduce((s, c) => s + (budget.limits[c] || 0), 0);
            const totalSpentAll = spendByCategory.reduce((s, [, amt]) => s + amt, 0);
            const remaining = budget.income - totalSpentAll;
            return (
              <div>
                <div style={{ ...S.grid4, gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)" }}>
                  <div style={S.statCard}>
                    <div style={S.statLabel}>Monthly Income</div>
                    <div style={S.statValue("#1D9E75")}>{fmt(budget.income)}</div>
                    <div style={S.statSub}>set below</div>
                  </div>
                  <div style={S.statCard}>
                    <div style={S.statLabel}>Total Budgeted</div>
                    <div style={S.statValue()}>{fmt(totalBudgeted)}</div>
                    <div style={S.statSub}>{allCats.filter(c => budget.limits[c] > 0).length} categories</div>
                  </div>
                  <div style={S.statCard}>
                    <div style={S.statLabel}>Total Spent</div>
                    <div style={S.statValue("#EF9F27")}>{fmt(totalSpentAll)}</div>
                    <div style={S.statSub}>this period</div>
                  </div>
                  <div style={S.statCard}>
                    <div style={S.statLabel}>Remaining</div>
                    <div style={S.statValue(remaining >= 0 ? "#1D9E75" : "#E24B4A")}>{fmt(Math.abs(remaining))}</div>
                    <div style={S.statSub}>{remaining >= 0 ? "left to spend" : "over budget"}</div>
                  </div>
                </div>

                <div style={{ ...S.grid2, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
                  <div style={S.card}>
                    <div style={S.cardTitle}>
                      Monthly Budget {budgetSaveStatus === "saving" ? "· Saving…" : budgetSaveStatus === "saved" ? "· Saved" : budgetSaveStatus === "error" ? "· Save failed" : ""}
                    </div>
                    <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid #222" }}>
                      <label style={S.label}>Monthly Income ($)</label>
                      <input
                        style={S.input}
                        type="number"
                        value={budget.income || ""}
                        onChange={e => setBudgetIncome(e.target.value)}
                        placeholder="Monthly take-home pay"
                      />
                    </div>
                    {CATEGORIES.map(cat => {
                      const limit = budget.limits[cat] || 0;
                      const spent = spendByCategory.find(([c]) => c === cat)?.[1] || 0;
                      const pct = limit > 0 ? Math.min((spent / limit) * 100, 100) : 0;
                      const over = limit > 0 && spent > limit;
                      return (
                        <div key={cat} style={{ marginBottom: 12 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: "#b8b5ae" }}>{cat}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{ fontSize: 11, color: "#555" }}>$</span>
                              <input
                                style={{ ...S.input, width: 80, padding: "4px 8px", fontSize: 12, textAlign: "right" }}
                                type="number"
                                value={limit || ""}
                                onChange={e => setBudgetLimit(cat, e.target.value)}
                                placeholder="0"
                              />
                            </div>
                          </div>
                          {limit > 0 && (
                            <>
                              <div style={{ height: 4, background: "#222", borderRadius: 2, marginBottom: 2 }}>
                                <div style={{ height: 4, borderRadius: 2, width: `${pct}%`, background: over ? "#E24B4A" : CAT_COLORS[cat] || "#555", transition: "width 0.4s" }} />
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#555" }}>
                                <span>{fmt(spent)} spent</span>
                                <span style={{ color: over ? "#E24B4A" : "#555" }}>{over ? `${fmt(spent - limit)} over` : `${fmt(limit - spent)} left`}</span>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div style={S.card}>
                    <div style={S.cardTitle}>Spending vs Budget</div>
                    {CATEGORIES.map(cat => {
                      const limit = budget.limits[cat] || 0;
                      const spent = spendByCategory.find(([c]) => c === cat)?.[1] || 0;
                      if (!limit && !spent) return null;
                      const pct = limit > 0 ? Math.min((spent / limit) * 100, 100) : 100;
                      const over = limit > 0 && spent > limit;
                      return (
                        <div key={cat} style={{ marginBottom: 14 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: "#b8b5ae" }}>{cat}</span>
                            <span style={{ fontSize: 12, color: over ? "#E24B4A" : "#1D9E75", fontWeight: 500 }}>
                              {fmt(spent)}{limit > 0 ? ` / ${fmt(limit)}` : ""}
                            </span>
                          </div>
                          <div style={{ height: 6, background: "#222", borderRadius: 3 }}>
                            <div style={{ height: 6, borderRadius: 3, width: `${limit > 0 ? pct : 50}%`, background: over ? "#E24B4A" : CAT_COLORS[cat] || "#555", transition: "width 0.4s" }} />
                          </div>
                        </div>
                      );
                    })}
                    {budget.income > 0 && (
                      <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #222" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ fontSize: 12, color: "#b8b5ae" }}>Net Savings</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: remaining >= 0 ? "#1D9E75" : "#E24B4A" }}>{fmt(remaining)}</span>
                        </div>
                        <div style={{ height: 6, background: "#222", borderRadius: 3 }}>
                          <div style={{ height: 6, borderRadius: 3, width: `${Math.min(Math.abs(remaining / budget.income) * 100, 100)}%`, background: remaining >= 0 ? "#1D9E75" : "#E24B4A" }} />
                        </div>
                        <div style={{ fontSize: 10, color: "#555", marginTop: 4 }}>{budget.income > 0 ? `${Math.round((remaining / budget.income) * 100)}% of income` : ""}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {activeTab === "payoff" && (
            <div style={{ ...S.grid2, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
              <div style={S.card}>
                <div style={S.cardTitle}>Payoff Timelines · Minimum Payments</div>
                {creditAccounts.length === 0 && <p style={{ fontSize: 12, color: "#555" }}>No credit accounts.</p>}
                {creditAccounts.map(a => {
                  const months = monthsToPayoff(a.balance, a.apr, a.minPayment);
                  const maxMonths = 60;
                  const pct = isFinite(months) ? Math.min((months / maxMonths) * 100, 100) : 100;
                  return (
                    <div key={a.id} style={S.payoffItem}>
                      <div style={S.payoffTop}>
                        <span style={S.payoffName}>{a.name}</span>
                        <span style={S.payoffDate}>{payoffDate(months)}</span>
                      </div>
                      <div style={S.payoffBarBg}>
                        <div style={{ height: 5, borderRadius: 3, width: `${pct}%`, background: a.color, transition: "width 0.4s" }} />
                      </div>
                      <div style={S.payoffMeta}>
                        <span>{fmt(a.balance)} remaining</span>
                        <span>{fmt(a.minPayment)}/mo · {a.apr}% APR</span>
                      </div>
                    </div>
                  );
                })}
                {creditAccounts.length > 1 && (() => {
                  const totalMonths = monthsToPayoff(totalDebt, weightedAPR, totalMinDue);
                  const pct = isFinite(totalMonths) ? Math.min((totalMonths / 60) * 100, 100) : 100;
                  return (
                    <div style={{ ...S.payoffItem, borderTop: "1px solid #2a2a2e", paddingTop: 14, marginTop: 6 }}>
                      <div style={S.payoffTop}>
                        <span style={{ ...S.payoffName, color: "#7F77DD" }}>All Accounts Combined</span>
                        <span style={S.payoffDate}>{payoffDate(totalMonths)}</span>
                      </div>
                      <div style={S.payoffBarBg}>
                        <div style={{ height: 5, borderRadius: 3, width: `${pct}%`, background: "#7F77DD", transition: "width 0.4s" }} />
                      </div>
                      <div style={S.payoffMeta}>
                        <span>{fmt(totalDebt)} total · {weightedAPR.toFixed(1)}% avg APR</span>
                        <span>{fmt(totalMinDue)}/mo minimum</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div style={S.card}>
                <div style={S.cardTitle}>Payment Simulator</div>
                <div style={S.formGroup}>
                  <label style={S.label}>Account</label>
                  <select style={S.select} value={simAcctId} onChange={e => {
                    const id = parseInt(e.target.value);
                    setSimAcctId(id);
                    if (id === 0) setSimPayment(totalMinDue);
                    else setSimPayment(accounts.find(a => a.id === id)?.minPayment || 100);
                  }}>
                    {creditAccounts.length > 1 && <option value={0}>All Accounts Combined</option>}
                    {creditAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                {simBalance > 0 && <>
                  <div style={S.formGroup}>
                    <label style={S.label}>Monthly Payment ($)</label>
                    <input
                      style={S.input}
                      type="number"
                      min={1}
                      value={simPayment}
                      onChange={e => setSimPayment(Math.max(1, parseFloat(e.target.value) || 0))}
                      placeholder={simMin}
                    />
                    {simMin > 0 && <div style={{ fontSize: 10, color: "#555", marginTop: 4 }}>Minimum: {fmt(simMin)}/mo</div>}
                  </div>

                  <div style={{ background: "#111113", borderRadius: 10, padding: "16px 18px", marginTop: 8 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <div style={S.statLabel}>Payoff Date</div>
                        <div style={{ fontSize: 18, fontWeight: 500, color: simColor }}>{payoffDate(simMonths)}</div>
                      </div>
                      <div>
                        <div style={S.statLabel}>Months</div>
                        <div style={{ fontSize: 18, fontWeight: 500, color: "#e8e6e0" }}>{isFinite(simMonths) ? simMonths : "∞"}</div>
                      </div>
                      <div>
                        <div style={S.statLabel}>Total Balance</div>
                        <div style={{ fontSize: 16, fontWeight: 500, color: "#E24B4A" }}>{fmt(simBalance)}</div>
                      </div>
                      <div>
                        <div style={S.statLabel}>{isAllAccounts ? "Avg APR" : "APR"}</div>
                        <div style={{ fontSize: 16, fontWeight: 500, color: "#e8e6e0" }}>{simAPR.toFixed(1)}%</div>
                      </div>
                    </div>
                    {!isFinite(simMonths) && (
                      <p style={{ fontSize: 11, color: "#E24B4A", marginTop: 12, marginBottom: 0 }}>
                        ⚠ Payment too low to cover interest — balance will grow indefinitely.
                      </p>
                    )}
                  </div>

                  {isAllAccounts && creditAccounts.length > 0 && (() => {
                    const extra = Math.max(0, simPayment - totalMinDue);
                    const underMin = simPayment < totalMinDue;
                    const sorted = [...creditAccounts].sort((a, b) => b.apr - a.apr);
                    const targetId = sorted[0]?.id;
                    const allocation = creditAccounts.map(a => ({
                      ...a,
                      alloc: (a.id === targetId ? (a.minPayment || 0) + extra : (a.minPayment || 0)),
                      isTarget: a.id === targetId,
                    }));
                    return (
                      <div style={{ marginTop: 14, background: "#111113", border: "1px solid #1e1e22", borderRadius: 10, padding: "16px 18px" }}>
                        <div style={{ fontSize: 10, color: "#7F77DD", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
                          Avalanche Allocation · {fmt(simPayment)}/mo
                        </div>

                        {/* Stacked bar */}
                        <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", marginBottom: 14, gap: 1 }}>
                          {allocation.map(a => (
                            <div
                              key={a.id}
                              title={`${a.name}: ${fmt(a.alloc)}`}
                              style={{
                                flex: a.alloc / simPayment,
                                background: a.color,
                                opacity: underMin ? 0.4 : 1,
                                minWidth: a.alloc > 0 ? 2 : 0,
                              }}
                            />
                          ))}
                        </div>

                        {/* Per-account rows */}
                        {allocation.map(a => {
                          const pct = simPayment > 0 ? Math.round((a.alloc / simPayment) * 100) : 0;
                          return (
                            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
                              <div style={{ width: 8, height: 8, borderRadius: "50%", background: a.color, flexShrink: 0 }} />
                              <span style={{ fontSize: 12, color: "#b8b5ae", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
                              <span style={{ fontSize: 10, color: "#444", marginRight: 4 }}>{pct}%</span>
                              <span style={{ fontSize: 12, fontWeight: 500, color: a.isTarget ? a.color : "#666", width: 64, textAlign: "right", flexShrink: 0 }}>{fmt(a.alloc)}</span>
                            </div>
                          );
                        })}

                        {/* Legend row */}
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #1e1e22", display: "flex", justifyContent: "space-between", fontSize: 10, color: "#444" }}>
                          <span>
                            {fmt(totalMinDue)} minimums
                            {extra > 0 && <> · <span style={{ color: sorted[0]?.color }}>{fmt(extra)} extra → {sorted[0]?.name}</span></>}
                          </span>
                          {underMin && <span style={{ color: "#E24B4A" }}>⚠ below minimums</span>}
                        </div>
                      </div>
                    );
                  })()}

                  {!isAllAccounts && creditAccounts.length > 0 && (
                    <div style={{ marginTop: 14, padding: "14px 16px", background: "#0f1a14", border: "1px solid #1a3022", borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: "#1D9E75", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Avalanche Tip</div>
                      <p style={{ fontSize: 12, color: "#888", margin: 0, lineHeight: 1.6 }}>
                        Target <strong style={{ color: "#e8e6e0" }}>{[...creditAccounts].sort((a, b) => b.apr - a.apr)[0]?.name}</strong> first
                        ({[...creditAccounts].sort((a, b) => b.apr - a.apr)[0]?.apr}% APR) to minimize total interest paid.
                      </p>
                    </div>
                  )}
                </>}
              </div>
            </div>
          )}
        </div>
      </div>

      {modal?.type === "account" && (
        <AccountModal account={modal.data} onSave={saveAccount} onClose={() => setModal(null)} />
      )}
      {modal?.type === "tx" && (
        <TxModal tx={modal.data} accounts={accounts} onSave={saveTx} onClose={() => setModal(null)} />
      )}
    </>
  );
}
